import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './BillingCadenceToggle.css';

export type BillingCadenceKey = 'monthly' | 'yearly';

interface BillingCadenceToggleProps {
  value: BillingCadenceKey;
  onChange: (next: BillingCadenceKey) => void;
  className?: string;
  ariaLabel?: string;
}

export const BillingCadenceToggle: React.FC<BillingCadenceToggleProps> = ({
  value,
  onChange,
  className,
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const monthlyRef = useRef<HTMLButtonElement>(null);
  const yearlyRef = useRef<HTMLButtonElement>(null);

  const wrapperClassName = className ? `billing-toggle ${className}` : 'billing-toggle';

  const selectCadence = (next: BillingCadenceKey) => {
    onChange(next);
    (next === 'monthly' ? monthlyRef : yearlyRef).current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    let next: BillingCadenceKey | undefined;

    if (event.key === 'Home') {
      next = 'monthly';
    } else if (event.key === 'End') {
      next = 'yearly';
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = value === 'monthly' ? 'yearly' : 'monthly';
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = value === 'yearly' ? 'monthly' : 'yearly';
    }

    if (next) {
      event.preventDefault();
      selectCadence(next);
    }
  };

  return (
    <div className={wrapperClassName}>
      <div className="billing-toggle-buttons" role="radiogroup" aria-label={ariaLabel}>
        <button
          ref={monthlyRef}
          type="button"
          role="radio"
          aria-checked={value === 'monthly'}
          tabIndex={value === 'monthly' ? 0 : -1}
          className={`billing-toggle-option${value === 'monthly' ? ' is-active' : ''}`}
          onClick={() => onChange('monthly')}
          onKeyDown={handleKeyDown}
        >
          {t('subscription.billing.monthly')}
        </button>
        <button
          ref={yearlyRef}
          type="button"
          role="radio"
          aria-checked={value === 'yearly'}
          tabIndex={value === 'yearly' ? 0 : -1}
          className={`billing-toggle-option${value === 'yearly' ? ' is-active' : ''}`}
          onClick={() => onChange('yearly')}
          onKeyDown={handleKeyDown}
        >
          {t('subscription.billing.yearly')}
        </button>
      </div>
    </div>
  );
};
